import React, { useCallback, useEffect, useState } from "react";
import {
  FaEye,
  FaExchangeAlt,
  FaCheck,
  FaBan,
  FaTimes,
  FaSearch,
  FaSyncAlt,
  FaPhone,
  FaEnvelope,
  FaWhatsapp,
} from "react-icons/fa";
import axiosInstance from "./api/axiosInstance";
import { formatPascal } from "../utils/formatters";
import TablePagination, { readStoredRowsPerPage } from "./TablePagination";
import { ResultsMobileCard, ResultsMobileList } from "./ResultsMobile";
import "../assets/css/WebsiteLeads.css";

const STATUS_TABS = ["New", "Reviewed", "Converted", "Rejected", "Spam", "All"];
const ROWS_PER_PAGE_KEY = "websiteLeadsRowsPerPage";

const STATUS_CARD_TONES = {
  New: "warning",
  Reviewed: "neutral",
  Converted: "success",
  Rejected: "danger",
  Spam: "danger",
};

const ACTION_LABELS = {
  join: "Joined waitlist",
  download_android: "Downloaded Android app",
};

function fullName(lead) {
  return formatPascal(`${(lead.First_Name || "").trim()} ${(lead.Last_Name || "").trim()}`.trim()) || "—";
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function phoneText(lead) {
  if (!lead.Mobile_Number) return "—";
  return [lead.Country_Code, lead.Mobile_Number].filter(Boolean).join(" ");
}

function labelize(key) {
  const text = String(key || "").replace(/_/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
}

function errorMessage(err, fallback) {
  const data = err?.response?.data;
  return data?.message || data?.error || fallback;
}

function StatusBadge({ status }) {
  return <span className={`wl-badge wl-badge--${String(status || "").toLowerCase()}`}>{status}</span>;
}

function DetailModal({ lead, showTenant, onClose }) {
  const extra = Object.entries(lead.Extra_Data || {}).filter(([, v]) => v !== null && v !== "");
  const rows = [
    ["Name", fullName(lead)],
    ["Mobile", phoneText(lead)],
    ["Email", lead.Email || "—"],
    showTenant ? ["Client", lead.Tenant_Name || `#${lead.Tenant_Id}`] : null,
    ["Website", lead.Source_Domain],
    ["Form", lead.Form_Name],
    ["Action", ACTION_LABELS[lead.Action] || lead.Action || "—"],
    ...extra.map(([k, v]) => [labelize(k), String(v)]),
    ["Page", lead.Page_Url || "—"],
    lead.Utm_Source || lead.Utm_Medium || lead.Utm_Campaign
      ? ["UTM", [lead.Utm_Source, lead.Utm_Medium, lead.Utm_Campaign].filter(Boolean).join(" / ")]
      : null,
    ["Submitted", formatDateTime(lead.Created_On)],
    ["Status", <StatusBadge key="s" status={lead.Status} />],
    lead.Reviewed_On ? ["Reviewed", `${formatDateTime(lead.Reviewed_On)}${lead.Reviewed_By_Name ? ` by ${lead.Reviewed_By_Name}` : ""}`] : null,
    lead.Converted_Lead_Id ? ["Lead", `#${lead.Converted_Lead_Id}`] : null,
  ].filter(Boolean);

  return (
    <div className="wl-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="wl-modal" role="dialog" aria-modal="true" aria-labelledby="wl-detail-title" onClick={(e) => e.stopPropagation()}>
        <div className="wl-modal-head">
          <h3 id="wl-detail-title">Website lead #{lead.Website_Lead_Id}</h3>
          <button type="button" className="wl-icon-btn" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <dl className="wl-detail-list">
          {rows.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      </div>
    </div>
  );
}

function ConvertModal({ lead, statuses, busy, error, onCancel, onConfirm }) {
  const defaultStatus = statuses.find((s) => String(s.Status).toLowerCase() === "new");
  const [statusId, setStatusId] = useState(defaultStatus ? String(defaultStatus.Status_Id) : "");
  const [notes, setNotes] = useState("");

  const submit = (e) => {
    e.preventDefault();
    onConfirm({
      Lead_Status_Id: statusId ? Number(statusId) : undefined,
      Notes: notes.trim() || undefined,
    });
  };

  return (
    <div className="wl-modal-backdrop" role="presentation" onClick={busy ? undefined : onCancel}>
      <form className="wl-modal" role="dialog" aria-modal="true" aria-labelledby="wl-convert-title" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="wl-modal-head">
          <h3 id="wl-convert-title">Convert to lead</h3>
          <button type="button" className="wl-icon-btn" onClick={onCancel} disabled={busy} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <p className="wl-muted">
          Creates a lead for <strong>{fullName(lead)}</strong> ({phoneText(lead)}) with source <strong>Website</strong>.
          The form answers are copied into the lead notes.
        </p>
        <label className="wl-field">
          <span>Lead status</span>
          <select value={statusId} onChange={(e) => setStatusId(e.target.value)} disabled={busy}>
            <option value="">Default (New)</option>
            {statuses.map((s) => (
              <option key={s.Status_Id} value={s.Status_Id}>
                {s.Status}
              </option>
            ))}
          </select>
        </label>
        <label className="wl-field">
          <span>Notes (optional)</span>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={busy} placeholder="e.g. Called, interested in a demo" />
        </label>
        {error ? <div className="wl-alert wl-alert--error">{error}</div> : null}
        <div className="wl-modal-actions">
          <button type="button" className="wl-btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="wl-btn wl-btn--primary" disabled={busy}>
            {busy ? "Converting…" : "Convert to lead"}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Website form submissions waiting for review.
 * Staff: apiBase="/website-leads" with the selected client's ``tenantId``. Tenant portal: apiBase="/client/website-leads".
 */
function WebsiteLeadsPanel({ apiBase, loadStatuses, tenantId, showTenant = false, onOpenLead }) {
  const [status, setStatus] = useState("New");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => readStoredRowsPerPage(ROWS_PER_PAGE_KEY));
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [detailLead, setDetailLead] = useState(null);
  const [convertLead, setConvertLead] = useState(null);
  const [convertError, setConvertError] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let active = true;
    if (!loadStatuses) return undefined;
    loadStatuses()
      .then((list) => active && setStatuses(Array.isArray(list) ? list : []))
      .catch(() => active && setStatuses([]));
    return () => {
      active = false;
    };
  }, [loadStatuses]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get(apiBase, {
        params: {
          tenant_id: tenantId || undefined,
          status: status === "All" ? undefined : status,
          search: search || undefined,
          page,
          page_size: rowsPerPage,
        },
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(Number(res.data?.total) || 0);
      setCounts(res.data?.counts || {});
    } catch (err) {
      setRows([]);
      setTotal(0);
      setError(errorMessage(err, "Could not load website leads."));
    } finally {
      setLoading(false);
    }
  }, [apiBase, tenantId, status, search, page, rowsPerPage]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const changeStatus = async (lead, nextStatus) => {
    setBusyId(lead.Website_Lead_Id);
    setNotice(null);
    try {
      await axiosInstance.put(`${apiBase}/${lead.Website_Lead_Id}/status`, { Status: nextStatus });
      setNotice({ type: "success", text: `${fullName(lead)} marked as ${nextStatus}.` });
      fetchRows();
    } catch (err) {
      setNotice({ type: "error", text: errorMessage(err, "Could not update the status.") });
    } finally {
      setBusyId(null);
    }
  };

  const confirmConvert = async (payload) => {
    if (!convertLead) return;
    setBusyId(convertLead.Website_Lead_Id);
    setConvertError("");
    try {
      const res = await axiosInstance.post(`${apiBase}/${convertLead.Website_Lead_Id}/convert`, payload);
      const newLeadId = res.data?.lead?.Real_Estate_Lead_Id;
      setNotice({
        type: "success",
        text: `${fullName(convertLead)} converted to lead${newLeadId ? ` #${newLeadId}` : ""}.`,
        leadId: newLeadId,
      });
      setConvertLead(null);
      fetchRows();
    } catch (err) {
      setConvertError(errorMessage(err, "Could not convert this website lead."));
    } finally {
      setBusyId(null);
    }
  };

  const allCount = Object.values(counts).reduce((sum, n) => sum + (Number(n) || 0), 0);

  return (
    <div className="wl-panel">
      <div className="wl-toolbar">
        <div className="wl-tabs" role="tablist" aria-label="Website lead status">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={status === tab}
              className={`wl-tab wl-tab--${tab.toLowerCase()}${status === tab ? " is-active" : ""}`}
              onClick={() => {
                setStatus(tab);
                setPage(1);
              }}
            >
              <span className="wl-tab-label">{tab}</span>
              <span className="wl-tab-count">{tab === "All" ? allCount : counts[tab] || 0}</span>
            </button>
          ))}
        </div>
        <div className="wl-toolbar-right">
          <label className="wl-search">
            <FaSearch aria-hidden="true" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name, mobile, email, website"
              aria-label="Search website leads"
            />
          </label>
          <button type="button" className="wl-icon-btn" onClick={fetchRows} title="Refresh" aria-label="Refresh">
            <FaSyncAlt />
          </button>
        </div>
      </div>

      {notice ? (
        <div className={`wl-alert wl-alert--${notice.type}`}>
          <span>{notice.text}</span>
          {notice.leadId && onOpenLead ? (
            <button type="button" className="wl-link-btn" onClick={() => onOpenLead(notice.leadId)}>
              Open lead
            </button>
          ) : null}
          <button type="button" className="wl-icon-btn" onClick={() => setNotice(null)} aria-label="Dismiss">
            <FaTimes />
          </button>
        </div>
      ) : null}
      {error ? <div className="wl-alert wl-alert--error">{error}</div> : null}

      <div className="wl-table-wrap co-desktop-table">
        <table className="wl-table">
          <thead>
            <tr>
              <th>Submitted</th>
              <th>Name</th>
              <th>Mobile</th>
              <th>Email</th>
              {showTenant ? <th>Client</th> : null}
              <th>Website</th>
              <th>Status</th>
              <th className="wl-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={showTenant ? 8 : 7} className="wl-empty">Loading…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={showTenant ? 8 : 7} className="wl-empty">
                  No {status === "All" ? "" : `${status.toLowerCase()} `}website leads.
                </td>
              </tr>
            ) : (
              rows.map((lead) => {
                const busy = busyId === lead.Website_Lead_Id;
                const open = lead.Status !== "Converted";
                return (
                  <tr key={lead.Website_Lead_Id}>
                    <td className="wl-nowrap">{formatDateTime(lead.Created_On)}</td>
                    <td>
                      <div className="wl-name">{fullName(lead)}</div>
                      {lead.Action ? <div className="wl-sub">{ACTION_LABELS[lead.Action] || lead.Action}</div> : null}
                    </td>
                    <td className="wl-nowrap">
                      {lead.Mobile_Number ? (
                        <a href={`tel:${lead.Country_Code || ""}${lead.Mobile_Number}`}>{phoneText(lead)}</a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{lead.Email ? <a href={`mailto:${lead.Email}`}>{lead.Email}</a> : "—"}</td>
                    {showTenant ? <td>{lead.Tenant_Name || `#${lead.Tenant_Id}`}</td> : null}
                    <td>
                      <div>{lead.Source_Domain}</div>
                      <div className="wl-sub">{lead.Form_Name}</div>
                    </td>
                    <td>
                      <StatusBadge status={lead.Status} />
                    </td>
                    <td className="wl-actions-col">
                      <div className="wl-actions">
                        <button type="button" className="wl-icon-btn" title="View" aria-label="View" onClick={() => setDetailLead(lead)}>
                          <FaEye />
                        </button>
                        {open ? (
                          <>
                            <button
                              type="button"
                              className="wl-btn wl-btn--primary wl-btn--sm"
                              disabled={busy}
                              onClick={() => {
                                setConvertError("");
                                setConvertLead(lead);
                              }}
                            >
                              <FaExchangeAlt aria-hidden="true" /> Convert
                            </button>
                            {lead.Status !== "Reviewed" ? (
                              <button type="button" className="wl-icon-btn" title="Mark reviewed" aria-label="Mark reviewed" disabled={busy} onClick={() => changeStatus(lead, "Reviewed")}>
                                <FaCheck />
                              </button>
                            ) : null}
                            {lead.Status !== "Rejected" ? (
                              <button type="button" className="wl-icon-btn" title="Reject" aria-label="Reject" disabled={busy} onClick={() => changeStatus(lead, "Rejected")}>
                                <FaTimes />
                              </button>
                            ) : null}
                            {lead.Status !== "Spam" ? (
                              <button type="button" className="wl-icon-btn wl-icon-btn--danger" title="Mark as spam" aria-label="Mark as spam" disabled={busy} onClick={() => changeStatus(lead, "Spam")}>
                                <FaBan />
                              </button>
                            ) : null}
                          </>
                        ) : lead.Converted_Lead_Id && onOpenLead ? (
                          <button type="button" className="wl-link-btn" onClick={() => onOpenLead(lead.Converted_Lead_Id)}>
                            Lead #{lead.Converted_Lead_Id}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ResultsMobileList
        loading={loading}
        emptyLabel={`No ${status === "All" ? "" : `${status.toLowerCase()} `}website leads.`}
        className="wl-mobile-results"
      >
        {rows.map((lead) => {
          const busy = busyId === lead.Website_Lead_Id;
          const open = lead.Status !== "Converted";
          const telHref = lead.Mobile_Number ? `tel:${lead.Country_Code || ""}${lead.Mobile_Number}` : null;
          const waDigits = `${lead.Country_Code || ""}${lead.Mobile_Number || ""}`.replace(/\D/g, "");
          const waHref = waDigits.length >= 10 ? `https://wa.me/${waDigits}` : null;
          const email = String(lead.Email || "").trim();
          const info = [
            { label: "Submitted", value: formatDateTime(lead.Created_On) },
            showTenant ? { label: "Client", value: lead.Tenant_Name || `#${lead.Tenant_Id}` } : null,
            lead.Source_Domain || lead.Form_Name
              ? { label: "Website", value: [lead.Source_Domain, lead.Form_Name].filter(Boolean).join(" · ") }
              : null,
          ].filter(Boolean);
          return (
            <ResultsMobileCard
              key={lead.Website_Lead_Id}
              title={fullName(lead)}
              meta={[
                { label: lead.Status, tone: STATUS_CARD_TONES[lead.Status] || "neutral", status: true },
                lead.Action ? { label: ACTION_LABELS[lead.Action] || lead.Action, tone: "info" } : null,
              ].filter(Boolean)}
              onOpen={() => setDetailLead(lead)}
              actions={
                <div className="co-mcard__action-grid">
                  {waHref ? (
                    <a
                      className="co-maction co-maction--whatsapp"
                      href={waHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="WhatsApp"
                      aria-label="WhatsApp"
                    >
                      <FaWhatsapp size={14} />
                      <span>Chat</span>
                    </a>
                  ) : null}
                  {open ? (
                    <>
                      <button
                        type="button"
                        className="co-maction"
                        disabled={busy}
                        onClick={() => {
                          setConvertError("");
                          setConvertLead(lead);
                        }}
                      >
                        <FaExchangeAlt size={13} />
                        <span>Convert</span>
                      </button>
                      {lead.Status !== "Reviewed" ? (
                        <button type="button" className="co-maction" disabled={busy} onClick={() => changeStatus(lead, "Reviewed")}>
                          <FaCheck size={13} />
                          <span>Review</span>
                        </button>
                      ) : null}
                      {lead.Status !== "Rejected" ? (
                        <button type="button" className="co-maction" disabled={busy} onClick={() => changeStatus(lead, "Rejected")}>
                          <FaTimes size={13} />
                          <span>Reject</span>
                        </button>
                      ) : null}
                      {lead.Status !== "Spam" ? (
                        <button type="button" className="co-maction co-maction--danger" disabled={busy} onClick={() => changeStatus(lead, "Spam")}>
                          <FaBan size={13} />
                          <span>Spam</span>
                        </button>
                      ) : null}
                    </>
                  ) : lead.Converted_Lead_Id && onOpenLead ? (
                    <button type="button" className="co-maction" onClick={() => onOpenLead(lead.Converted_Lead_Id)}>
                      <FaExchangeAlt size={13} />
                      <span>Lead #{lead.Converted_Lead_Id}</span>
                    </button>
                  ) : null}
                </div>
              }
            >
              {telHref || email ? (
                <ul className="co-tcard__contact">
                  {telHref ? (
                    <li>
                      <FaPhone aria-hidden="true" style={{ transform: "scaleX(-1)" }} />
                      <a href={telHref} title={`Call ${phoneText(lead)}`}>
                        {phoneText(lead)}
                      </a>
                    </li>
                  ) : null}
                  {email ? (
                    <li>
                      <FaEnvelope aria-hidden="true" />
                      <a href={`mailto:${email}`} title={`Email ${email}`}>
                        {email}
                      </a>
                    </li>
                  ) : null}
                </ul>
              ) : null}
              <dl className="co-tcard__info">
                {info.map((item, i) => (
                  <div
                    key={item.label}
                    className={
                      info.length % 2 === 1 && i === info.length - 1 ? "co-tcard__info-full" : undefined
                    }
                  >
                    <dt>{item.label}</dt>
                    <dd>{item.value}</dd>
                  </div>
                ))}
              </dl>
            </ResultsMobileCard>
          );
        })}
      </ResultsMobileList>

      <TablePagination
        currentPage={page}
        totalItems={total}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(1);
          try {
            localStorage.setItem(ROWS_PER_PAGE_KEY, String(n));
          } catch {
            /* ignore */
          }
        }}
        itemLabel="website leads"
        rowsPerPageSelectId="website-leads-rows-per-page"
      />

      {detailLead ? <DetailModal lead={detailLead} showTenant={showTenant} onClose={() => setDetailLead(null)} /> : null}
      {convertLead ? (
        <ConvertModal
          lead={convertLead}
          statuses={statuses}
          busy={busyId === convertLead.Website_Lead_Id}
          error={convertError}
          onCancel={() => setConvertLead(null)}
          onConfirm={confirmConvert}
        />
      ) : null}
    </div>
  );
}

export default WebsiteLeadsPanel;
